import React, { useState, useEffect } from 'react';
import { supabase } from '../supabaseClient';

const TimesheetSyncView = ({ onBack }) => {
  const [syncLogs, setSyncLogs] = useState([]);
  const [summaryData, setSummaryData] = useState([]);
  const [attendanceData, setAttendanceData] = useState([]);
  const [selectedPayroll, setSelectedPayroll] = useState(null);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [view, setView] = useState('logs'); // 'logs' | 'summary' | 'attendance'

  useEffect(() => {
    fetchSyncLogs();
  }, []);

  const fetchSyncLogs = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('timesheet_sync_log')
        .select('*')
        .order('synced_at', { ascending: false })
        .limit(20);

      if (error) throw error;
      setSyncLogs(data || []);

      // Auto-select most recent sync if available
      if (data && data.length > 0) {
        handleSelectPayroll(data[0].payroll_number);
      }
    } catch (err) {
      console.error('Error fetching sync logs:', err);
      setError('Failed to load sync logs');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectPayroll = async (payrollNumber) => {
    try {
      setLoading(true);
      setSelectedPayroll(payrollNumber);
      setSelectedEmployee(null);
      setView('summary');

      // Fetch summary data for selected payroll
      const { data: summary, error: summaryError } = await supabase
        .from('timesheet_sync_summary')
        .select('*')
        .eq('payroll_number', payrollNumber)
        .order('employee_name');

      if (summaryError) throw summaryError;
      setSummaryData(summary || []);

      // Fetch all attendance for this payroll
      const { data: attendance, error: attendanceError } = await supabase
        .from('timesheet_sync_attendance')
        .select('*')
        .eq('payroll_number', payrollNumber)
        .order('date');

      if (attendanceError) throw attendanceError;
      setAttendanceData(attendance || []);
    } catch (err) {
      console.error('Error fetching payroll data:', err);
      setError('Failed to load payroll data');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectEmployee = (employeeNumber) => {
    setSelectedEmployee(employeeNumber);
    setView('attendance');
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr + 'T00:00:00');
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getDayName = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr + 'T00:00:00');
    return date.toLocaleDateString('en-US', { weekday: 'short' });
  };

  const employeeAttendance = selectedEmployee
    ? attendanceData.filter(a => a.employee_number === selectedEmployee)
    : [];

  const getCompanyBadgeColor = (company) => {
    return company === 'KF' ? 'bg-blue-100 text-blue-800' : 'bg-green-100 text-green-800';
  };

  const getStatusBadgeColor = (status) => {
    switch (status) {
      case 'success': return 'bg-green-100 text-green-800';
      case 'partial': return 'bg-yellow-100 text-yellow-800';
      case 'failed': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading && syncLogs.length === 0) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 mx-auto"></div>
          <p className="mt-2 text-gray-500">Loading sync data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white shadow rounded-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">Timesheet Sync Data</h2>
            <p className="text-sm text-gray-500 mt-1">
              View hours synced from Rx-Manager Timesheet Module
            </p>
          </div>
          <button
            onClick={onBack}
            className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600 transition-colors"
          >
            ← Back to Dashboard
          </button>
        </div>

        {error && (
          <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md">
            {error}
          </div>
        )}

        {/* Breadcrumb Navigation */}
        <div className="flex items-center space-x-2 text-sm text-gray-500">
          <button
            onClick={() => setView('logs')}
            className={`hover:text-indigo-600 ${view === 'logs' ? 'text-indigo-600 font-medium' : ''}`}
          >
            Sync History
          </button>
          {selectedPayroll && (
            <>
              <span>→</span>
              <button
                onClick={() => { setView('summary'); setSelectedEmployee(null); }}
                className={`hover:text-indigo-600 ${view === 'summary' ? 'text-indigo-600 font-medium' : ''}`}
              >
                Payroll #{selectedPayroll}
              </button>
            </>
          )}
          {selectedEmployee && (
            <>
              <span>→</span>
              <span className="text-indigo-600 font-medium">
                {summaryData.find(s => s.employee_number === selectedEmployee)?.employee_name || selectedEmployee}
              </span>
            </>
          )}
        </div>
      </div>

      {/* Sync Logs View */}
      {view === 'logs' && (
        <div className="bg-white shadow rounded-lg overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-medium text-gray-900">Recent Syncs</h3>
          </div>
          {syncLogs.length === 0 ? (
            <div className="text-center py-12">
              <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              <h3 className="mt-2 text-sm font-medium text-gray-900">No sync data yet</h3>
              <p className="mt-1 text-sm text-gray-500">
                Sync timesheet data from Rx-Manager to see it here.
              </p>
            </div>
          ) : (
            <div className="divide-y divide-gray-200">
              {syncLogs.map((log) => (
                <div
                  key={log.id}
                  onClick={() => handleSelectPayroll(log.payroll_number)}
                  className="px-6 py-4 hover:bg-gray-50 cursor-pointer transition-colors"
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="flex items-center space-x-3">
                        <span className="text-lg font-medium text-gray-900">
                          Payroll #{log.payroll_number}
                        </span>
                        <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusBadgeColor(log.status)}`}>
                          {log.status}
                        </span>
                      </div>
                      <div className="text-sm text-gray-500 mt-1">
                        {log.employees_synced} employees • {log.attendance_records} attendance records
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-sm text-gray-900">{formatDateTime(log.synced_at)}</div>
                      <div className="text-xs text-gray-500">
                        {log.sync_type === 'manual' ? 'Manual sync' : 'Scheduled'}
                        {log.synced_by && ` by ${log.synced_by}`}
                      </div>
                    </div>
                  </div>
                  {log.error_message && (
                    <div className="mt-2 text-sm text-red-600">
                      Error: {log.error_message}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Summary View */}
      {view === 'summary' && selectedPayroll && (
        <div className="bg-white shadow rounded-lg overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
            <div>
              <h3 className="text-lg font-medium text-gray-900">
                Payroll #{selectedPayroll} Summary
              </h3>
              <p className="text-sm text-gray-500">
                {summaryData.length} employees synced
              </p>
            </div>
            <div className="text-right">
              <div className="text-2xl font-bold text-gray-900">
                {summaryData.reduce((sum, s) => sum + parseFloat(s.total_hours || 0), 0).toFixed(1)}h
              </div>
              <div className="text-sm text-gray-500">Total Hours</div>
            </div>
          </div>

          {loading ? (
            <div className="flex justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
            </div>
          ) : summaryData.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-gray-500">No data found for this payroll period.</p>
            </div>
          ) : (
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Employee
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Company
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Days Worked
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Total Hours
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {summaryData.map((summary) => (
                  <tr key={summary.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="h-8 w-8 rounded-full bg-indigo-100 flex items-center justify-center">
                          <span className="text-indigo-600 font-medium text-xs">
                            {summary.employee_name?.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)}
                          </span>
                        </div>
                        <div className="ml-3">
                          <div className="text-sm font-medium text-gray-900">{summary.employee_name}</div>
                          <div className="text-xs text-gray-500">{summary.employee_number}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${getCompanyBadgeColor(summary.company)}`}>
                        {summary.company}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm text-gray-900">
                      {summary.days_worked || '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right">
                      <span className="text-lg font-semibold text-gray-900">
                        {parseFloat(summary.total_hours || 0).toFixed(1)}h
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right">
                      <button
                        onClick={() => handleSelectEmployee(summary.employee_number)}
                        className="text-indigo-600 hover:text-indigo-900 text-sm font-medium"
                      >
                        View Details →
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* Attendance Detail View */}
      {view === 'attendance' && selectedEmployee && (
        <div className="bg-white shadow rounded-lg overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-medium text-gray-900">
                  Daily Attendance
                </h3>
                <p className="text-sm text-gray-500">
                  {summaryData.find(s => s.employee_number === selectedEmployee)?.employee_name} - Payroll #{selectedPayroll}
                </p>
              </div>
              <button
                onClick={() => { setView('summary'); setSelectedEmployee(null); }}
                className="text-sm text-indigo-600 hover:text-indigo-900"
              >
                ← Back to Summary
              </button>
            </div>
          </div>

          {employeeAttendance.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-gray-500">No attendance records found.</p>
            </div>
          ) : (
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Date
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Day
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Store
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Company
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Hours
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {employeeAttendance.map((record) => (
                  <tr key={record.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {formatDate(record.date)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {getDayName(record.date)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {record.store_code || '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${getCompanyBadgeColor(record.company)}`}>
                        {record.company}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right">
                      <span className="text-lg font-semibold text-gray-900">
                        {parseFloat(record.hours || 0).toFixed(1)}h
                      </span>
                    </td>
                  </tr>
                ))}
                {/* Total Row */}
                <tr className="bg-gray-50 font-medium">
                  <td colSpan="4" className="px-6 py-4 text-right text-sm text-gray-900">
                    Total Hours:
                  </td>
                  <td className="px-6 py-4 text-right">
                    <span className="text-lg font-bold text-indigo-600">
                      {employeeAttendance.reduce((sum, r) => sum + parseFloat(r.hours || 0), 0).toFixed(1)}h
                    </span>
                  </td>
                </tr>
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
};

export default TimesheetSyncView;
